"use client";

/**
 * Landing Page CTA Components with Analytics Tracking
 *
 * These are client components that wrap the landing page CTAs
 * with GA4 event tracking.
 */

import { ArrowRight, Mail } from "lucide-react";
import Link from "next/link";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { CTA_IDS, PLACEMENTS, trackCtaClick } from "@/lib/analytics";

// =============================================================================
// Navigation Get Started Button
// =============================================================================

export function NavGetStartedButton() {
  const handleClick = () => {
    trackCtaClick({
      cta_id: CTA_IDS.NAV_GET_STARTED,
      placement: PLACEMENTS.HEADER,
      destination: "/app",
    });
  };

  return (
    <Button
      asChild
      className="bg-primary text-primary-foreground hover:bg-accent"
      onClick={handleClick}
    >
      <Link href="/app">Get Started</Link>
    </Button>
  );
}

// =============================================================================
// Navigation Links
// =============================================================================

type NavLinkProps = {
  href: string;
  ctaId: string;
  children: ReactNode;
};

export function NavLink({ href, ctaId, children }: NavLinkProps) {
  const handleClick = () => {
    trackCtaClick({
      cta_id: ctaId,
      placement: PLACEMENTS.HEADER,
      destination: href,
    });
  };

  return (
    <a
      className="text-muted-foreground text-sm transition hover:text-foreground"
      href={href}
      onClick={handleClick}
    >
      {children}
    </a>
  );
}

// =============================================================================
// Hero CTAs
// =============================================================================

export function HeroPrimaryButton() {
  const handleClick = () => {
    trackCtaClick({
      cta_id: CTA_IDS.HERO_PRIMARY,
      placement: PLACEMENTS.HERO,
      destination: "/app",
    });
  };

  return (
    <Button
      asChild
      className="h-12 gap-2 bg-primary px-8 text-base text-primary-foreground hover:bg-accent"
      onClick={handleClick}
    >
      <Link href="/app">
        Start Exploring <ArrowRight className="h-4 w-4" />
      </Link>
    </Button>
  );
}

export function HeroSecondaryButton() {
  const handleClick = () => {
    trackCtaClick({
      cta_id: CTA_IDS.HERO_SECONDARY,
      placement: PLACEMENTS.HERO,
      destination: "#features",
    });
  };

  return (
    <Button
      asChild
      className="h-12 border-border bg-transparent px-8 text-base text-foreground hover:bg-secondary/20"
      onClick={handleClick}
      variant="outline"
    >
      <a href="#features">See Features</a>
    </Button>
  );
}

// =============================================================================
// Example Query Links
// =============================================================================

type ExampleQueryLinkProps = {
  query: string;
};

export function ExampleQueryLink({ query }: ExampleQueryLinkProps) {
  const handleClick = () => {
    trackCtaClick({
      cta_id: CTA_IDS.HERO_TRY_EXAMPLE,
      placement: PLACEMENTS.LANDING,
      destination: `/app?query=${encodeURIComponent(query)}`,
    });
  };

  return (
    <Link
      className="group flex items-center justify-between rounded-lg border border-border bg-secondary/20 p-4 transition-all duration-300 hover:border-primary/50 hover:bg-secondary/40"
      href={`/app?query=${encodeURIComponent(query)}`}
      onClick={handleClick}
    >
      <span className="text-foreground">{query}</span>
      <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
    </Link>
  );
}

// =============================================================================
// Try Own Query Button
// =============================================================================

export function TryOwnQueryButton() {
  const handleClick = () => {
    trackCtaClick({
      cta_id: CTA_IDS.HERO_PRIMARY,
      placement: PLACEMENTS.LANDING,
      destination: "/app",
    });
  };

  return (
    <Button
      asChild
      className="h-12 gap-2 bg-primary px-8 text-base text-primary-foreground hover:bg-accent"
      onClick={handleClick}
    >
      <Link href="/app">
        Try Your Own Query <ArrowRight className="h-4 w-4" />
      </Link>
    </Button>
  );
}

// =============================================================================
// Launch TXGPT CTA
// =============================================================================

export function LaunchCtaButton() {
  const handleClick = () => {
    trackCtaClick({
      cta_id: CTA_IDS.LANDING_LAUNCH,
      placement: PLACEMENTS.LANDING,
      destination: "/app",
    });
  };

  return (
    <Button
      asChild
      className="h-12 gap-2 bg-primary px-8 text-base text-primary-foreground hover:bg-accent"
      onClick={handleClick}
    >
      <Link href="/app">
        Launch TXGPT <ArrowRight className="h-4 w-4" />
      </Link>
    </Button>
  );
}

// =============================================================================
// About Section CTAs
// =============================================================================

export function ContactButton() {
  const handleClick = () => {
    trackCtaClick({
      cta_id: CTA_IDS.LANDING_CONTACT,
      placement: PLACEMENTS.LANDING,
      destination: "https://x.com/MbBrainz",
    });
  };

  return (
    <Button
      asChild
      className="h-12 gap-2 bg-primary px-8 text-base text-primary-foreground hover:bg-accent"
      onClick={handleClick}
    >
      <a
        href="https://x.com/MbBrainz"
        rel="noopener noreferrer"
        target="_blank"
      >
        <Mail className="h-5 w-5" />
        Get in Touch
      </a>
    </Button>
  );
}

export function TwitterButton() {
  const handleClick = () => {
    trackCtaClick({
      cta_id: CTA_IDS.LANDING_TWITTER,
      placement: PLACEMENTS.LANDING,
      destination: "https://x.com/MbBrainz",
    });
  };

  return (
    <Button
      asChild
      className="h-12 gap-2 border-border bg-transparent px-8 text-base text-foreground hover:bg-secondary/30"
      onClick={handleClick}
      variant="outline"
    >
      <a
        href="https://x.com/MbBrainz"
        rel="noopener noreferrer"
        target="_blank"
      >
        Follow on X
      </a>
    </Button>
  );
}

export function GitHubButton() {
  const handleClick = () => {
    trackCtaClick({
      cta_id: CTA_IDS.LANDING_GITHUB,
      placement: PLACEMENTS.LANDING,
      destination: "https://github.com/MbBrainz",
    });
  };

  return (
    <Button
      asChild
      className="h-12 gap-2 border-border bg-transparent px-8 text-base text-foreground hover:bg-secondary/30"
      onClick={handleClick}
      variant="outline"
    >
      <a
        href="https://github.com/MbBrainz"
        rel="noopener noreferrer"
        target="_blank"
      >
        View GitHub
      </a>
    </Button>
  );
}
